import React, { useEffect, useState } from "react";
import { Task } from "../../types/api";
import { useEquipmentStore, useTaskStore } from "../../store/store";
import { TASK_MODULE_LIST_FIRE_SAFETY } from "../../constants/modules";

interface FiltersProps {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  tasks: Task[];
  setFilterCount: React.Dispatch<React.SetStateAction<number>>;
}

const Filters: React.FC<FiltersProps> = ({
  showModal,
  setShowModal,
  tasks,
  setFilterCount,
}) => {
  const [filterData, setFilterData] = useState({
    topic: "",
    frequency: "",
    module: "", // Added module filter
  });

  const { filterTask } = useTaskStore();
  const { equipmentData, allAssetsEquipment } = useEquipmentStore();

  const getEquipmentName = (id: string): string => {
    const equipment = equipmentData?.find((eq: any) => eq._id === id);
    return equipment ? equipment.equipmentName : id || "N/A";
  };

  // Get unique topics from tasks
  const topics = React.useMemo(() => {
    const topicIds = Array.from(
      new Set(tasks.map((task) => task.topic))
    ).filter(Boolean);
    return topicIds.map((id) => ({
      value: id,
      label: getEquipmentName(id),
    }));
  }, [tasks, equipmentData]);

  // Get unique frequencies from tasks
  const frequencies = Array.from(
    new Set(tasks.map((task) => task.frequency))
  ).filter(Boolean);

  // Get unique modules from tasks
  const modules = React.useMemo(() => {
    const moduleValues = Array.from(
      new Set(tasks.map((task) => task.module))
    ).filter(Boolean);

    // Map module values to their labels using TASK_MODULE_LIST_FIRE_SAFETY
    return moduleValues.map((moduleValue) => {
      const moduleConfig = TASK_MODULE_LIST_FIRE_SAFETY.find(
        (mod) => mod.value === moduleValue
      );
      return {
        value: moduleValue as string,
        label: (moduleConfig ? moduleConfig.label : moduleValue) as string,
      };
    });
  }, [tasks]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilterData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Update filter count whenever filterData changes
  useEffect(() => {
    const count = Object.values(filterData).filter(
      (value) => value !== ""
    ).length;
    setFilterCount(count);
  }, [filterData, setFilterCount]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prepare filter data - only include non-empty values
    const activeFilters: any = {};
    if (filterData.topic) activeFilters.topic = filterData.topic;
    if (filterData.frequency) activeFilters.frequency = filterData.frequency;
    if (filterData.module) activeFilters.module = filterData.module;

    await filterTask(activeFilters);
    setShowModal(false);
  };

  const handleReset = () => {
    setFilterData({
      topic: "",
      frequency: "",
      module: "",
    });
    setFilterCount(0);
  };

  const renderSelect = (
    label: string,
    name: string,
    options: { value: string; label: string }[] | string[],
    value: string
  ) => (
    <div>
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      <select
        className="w-full border-2 border-[#a5b7cf] rounded font-semibold p-2 mt-1"
        name={name}
        value={value}
        onChange={handleChange}
      >
        <option value="">{`Select ${label}`}</option>
        {Array.isArray(options) && options.length > 0
          ? options.map((option, index) => (
              <option
                key={index}
                value={typeof option === "string" ? option : option.value}
              >
                {typeof option === "string" ? option : option.label}
              </option>
            ))
          : null}
      </select>
    </div>
  );

  return (
    <>
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-end z-[100001]">
          <div className="w-[400px] bg-white shadow-xl flex flex-col h-full modal-slide">
            <div className="flex justify-between items-center w-full px-3 py-2 border-b">
              <h2 className="text-lg font-semibold">Filters</h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-xl px-3 focus:outline-none hover:bg-[#0fa9a6] rounded-full hover:text-white font-semibold text-black"
              >
                X
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-2">
              <form
                onSubmit={handleSubmit}
                className="flex flex-col gap-5"
                id="form-id"
              >
                {/* Module Filter */}
                {renderSelect("Module", "module", modules, filterData.module)}

                {/* Topic Filter */}
                {renderSelect("Topic", "topic", topics, filterData.topic)}

                {/* Frequency Filter */}
                {renderSelect(
                  "Frequency",
                  "frequency",
                  frequencies,
                  filterData.frequency
                )}
              </form>
            </div>

            <div className="w-full px-3 py-4 bg-[#eef4f8] text-[#566680] border-t flex gap-2">
              <button
                type="button"
                onClick={handleReset}
                className="bg-gray-300 text-gray-700 py-2 px-4 rounded-full hover:bg-gray-400 flex-1"
              >
                Reset
              </button>
              <button
                type="submit"
                form="form-id"
                className="bg-teal-500 text-white py-2 px-4 rounded-full hover:bg-teal-600 flex-1"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Filters;
